import { useState, useMemo, useEffect } from 'react';
import { X, AlertCircle, Printer } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { posShifts } from '../../api/posAPI';
import api from '../../api/axiosConfig';
import usePosStore from '../../stores/posStore';
import { useShiftSessionStore } from '../../features/pos/stores/shiftSessionStore';

const BILLS = [1000, 500, 200, 100, 50, 20, 10, 5, 1];

function fmtAmt(centavos) {
  return `₱${((centavos || 0) / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CloseShiftModal({ onClose, onClosed }) {
  const { posToken, activeShift, clearPosSession } = usePosStore();
  const { activeSession, clearSession } = useShiftSessionStore();
  const [counts, setCounts] = useState(Object.fromEntries(BILLS.map(b => [b, 0])));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Fetch live sales summary from POS API
  useEffect(() => {
    if (!posToken || !activeShift?._id) return;
    let cancelled = false;
    setSummaryLoading(true);
    posShifts.getSummary(posToken, activeShift._id)
      .then(res => { if (!cancelled) setSummary(res.data?.summary || null); })
      .catch(() => { /* fall back to store values silently */ })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [posToken, activeShift?._id]);

  const closingCash = useMemo(
    () => BILLS.reduce((s, b) => s + b * 100 * (parseInt(counts[b]) || 0), 0),
    [counts]
  );

  const openingFloat  = activeShift?.openingFloat || 0;
  const cashSales     = summary?.cashSales ?? activeShift?.totalCashSales ?? 0;
  const expectedCash  = openingFloat + cashSales;
  const overShort     = closingCash - expectedCash;

  const overShortLabel = overShort === 0
    ? { text: 'BALANCED ✓', cls: 'text-green-700' }
    : overShort > 0
    ? { text: `OVER +${fmtAmt(overShort)}`, cls: 'text-amber-600' }
    : { text: `SHORT ${fmtAmt(overShort)}`, cls: 'text-red-600' };

  const currentDenominations = () =>
    BILLS.map(b => ({ bill: b, count: parseInt(counts[b]) || 0 })).filter(d => d.count > 0);

  const handleClose = async () => {
    setError(null);
    setLoading(true);
    try {
      const denominations = currentDenominations();
      if (!denominations.length) {
        setError('Enter at least one denomination count');
        setLoading(false);
        return;
      }

      // Close the inner PosShift (POS JWT) — prints Z-reading, records denominations
      await posShifts.close(posToken, { denominations, closingNotes: notes });

      // Close the outer PosShiftSession (main JWT) — resets session continuity
      if (activeSession?._id) {
        try {
          await api.post(`/pos/shift-session/${activeSession._id}/close`, {
            closingCash,
          });
        } catch (sessionErr) {
          console.warn('PosShiftSession close failed:', sessionErr?.response?.data?.message || sessionErr.message);
        }
        clearSession();
      }

      clearPosSession();
      onClosed?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to close shift');
      setConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReport = async () => {
    setError(null);
    setPrinting(true);
    try {
      const denominations = BILLS.map(b => ({ bill: b, count: parseInt(counts[b]) || 0 }));
      await posShifts.printReport(posToken, activeShift._id, denominations);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  const startedAt = activeShift?.startedAt ? new Date(activeShift.startedAt) : null;
  const duration  = startedAt ? formatDistanceToNow(startedAt) : '—';

  const nonCashMethods = ['gcash', 'maya', 'card', 'charge'];
  const nonCashRows = summary
    ? nonCashMethods
        .map(m => ({ m, amt: summary.byPaymentMethod?.[m] || 0 }))
        .filter(r => r.amt > 0)
    : [];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-900 text-base">Close Shift</h2>
            <div className="text-xs text-gray-500 mt-0.5 space-x-3">
              {activeShift?.cashierName && <span>Cashier: <strong className="text-gray-700">{activeShift.cashierName}</strong></span>}
              {activeShift?.terminalId   && <span>Terminal: <strong className="text-gray-700">{activeShift.terminalId}</strong></span>}
              {startedAt && (
                <span>
                  Started: <strong className="text-gray-700">{format(startedAt, 'h:mm a')}</strong>{' '}
                  <span className="text-gray-400">({duration} ago)</span>
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 mt-0.5">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Section A — Denomination input */}
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Declare Closing Cash</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-200">
                  <th className="text-left pb-1.5 font-medium">Bill / Coin</th>
                  <th className="text-center pb-1.5 w-20 font-medium">Count</th>
                  <th className="text-right pb-1.5 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {BILLS.map(b => {
                  const count = parseInt(counts[b]) || 0;
                  return (
                    <tr key={b} className="border-b border-gray-100">
                      <td className="py-1.5 font-medium text-gray-700">₱{b.toLocaleString()}</td>
                      <td className="py-1.5 text-center">
                        <input
                          type="number"
                          min="0"
                          value={counts[b]}
                          onChange={e => setCounts(prev => ({ ...prev, [b]: e.target.value }))}
                          className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        />
                      </td>
                      <td className="py-1.5 text-right text-gray-600">
                        {count > 0 ? fmtAmt(b * count * 100) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td colSpan={2} className="pt-2 font-bold text-gray-900">Total Declared Cash</td>
                  <td className="pt-2 text-right font-bold text-gray-900">{fmtAmt(closingCash)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Section B — Balance summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Shift Balance Summary</p>

            <Row label="Opening Float" value={fmtAmt(openingFloat)} />
            <Row label={summaryLoading ? 'Cash Sales (loading…)' : 'Cash Sales'} value={fmtAmt(cashSales)} />
            <div className="border-t border-gray-200 my-1" />
            <Row label="Expected Cash" value={fmtAmt(expectedCash)} bold />
            <Row label="Declared Cash" value={fmtAmt(closingCash)} bold />
            <div className={`flex justify-between font-bold border-t border-gray-200 pt-1.5 ${overShortLabel.cls}`}>
              <span>Over / Short</span>
              <span>{overShortLabel.text}</span>
            </div>

            {nonCashRows.length > 0 && (
              <>
                <div className="border-t border-gray-200 mt-2 pt-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Non-Cash (for reference)</p>
                  {nonCashRows.map(({ m, amt }) => (
                    <Row
                      key={m}
                      label={m === 'charge' ? 'Charge (to AR)' : m.charAt(0).toUpperCase() + m.slice(1)}
                      value={fmtAmt(amt)}
                    />
                  ))}
                </div>
              </>
            )}

            {summary && (
              <>
                <div className="border-t border-gray-200 mt-2 pt-2">
                  <Row label="Gross Sales" value={fmtAmt(summary.grossSales)} bold />
                  <Row label={`Discounts`} value={`-${fmtAmt(summary.totalDiscount)}`} />
                  <Row label="Net Sales" value={fmtAmt(summary.netSales)} bold />
                  <Row label="Transactions" value={String(summary.totalTransactions)} />
                </div>
              </>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Closing Notes <span className="font-normal text-gray-400">(optional)</span></label>
            <textarea
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any closing notes…"
            />
          </div>

          {/* Confirm step */}
          {confirm && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
              <p className="font-semibold text-amber-800 mb-3">
                This will end the shift and print the Z reading. Continue?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirm(false)}
                  disabled={loading}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
                >
                  Go Back
                </button>
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50"
                >
                  {loading ? 'Closing…' : 'Yes, Close Shift'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {!confirm && (
          <div className="flex gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onClose}
              disabled={loading}
              className="py-2.5 px-4 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePrintReport}
              disabled={loading || printing || !activeShift?._id}
              className="py-2.5 px-4 border border-blue-300 text-blue-700 rounded-xl text-sm hover:bg-blue-50 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Printer className="h-4 w-4" />
              {printing ? 'Printing…' : 'Print X Reading'}
            </button>
            <button
              onClick={() => { setError(null); setConfirm(true); }}
              disabled={loading || printing}
              className="ml-auto py-2.5 px-5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
            >
              Close Shift & Print Z
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
